import { useNavigate } from "@tanstack/react-router";
import React, { useEffect, useRef, useState } from "react";
import Markdown, { defaultUrlTransform } from "react-markdown";
import rehypeExternalLinks from "rehype-external-links";
import remarkDirective from "remark-directive";
import remarkGfm from "remark-gfm";
import { MarkdownTable } from "@compass/shared/components/charts";
import {
    contentRefFromHref,
    contentRefWebPath,
    isContentLinkScheme,
    normalizeBlogPostEmbedMarkdown,
    rehypeIndentation,
    rehypeUnderline,
    remarkBlogPostEmbeds,
} from "@compass/shared/utils";

const MARKDOWN_TABLE_RE = /^\s*\|.+\|/m;

// react-markdown blanks hrefs outside its protocol allow-list; keep ours.
const keepContentLinkUrls = (url: string) =>
    isContentLinkScheme(url) ? url : defaultUrlTransform(url);

const PROSE_CLASS = `prose prose-sm [&_.indent-level-3]:ml-15 [&_.indent-level-5]:ml-25 max-w-none  list-inside list-disc whitespace-pre-wrap text-[#424242] **:wrap-anywhere [&_.indent-level-1]:ml-5 [&_.indent-level-2]:ml-10 [&_.indent-level-4]:ml-20 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_blockquote]:ml-0 [&_blockquote]:mb-3 [&_blockquote]:border-0 [&_blockquote]:border-l-8 [&_blockquote]:border-solid [&_blockquote]:border-l-gray-200 [&_blockquote]:bg-[#f8f8f9] [&_blockquote]:py-1 [&_blockquote]:pl-2 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_code]:text-pink-600 [&_li]:ml-2 [&_ol]:ml-6 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:whitespace-normal [&_pre]:rounded [&_pre]:bg-[#f8f8f9] [&_pre]:p-2 [&_ul]:ml-6 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:whitespace-normal`;

interface MarkdownContentProps {
    markdown: string;
    className?: string;
    /** When true, renders full content without truncation or "Show more". */
    expanded?: boolean;
    onShowMore?: () => void;
}

export const MarkdownContent = ({
    markdown,
    className,
    expanded,
    onShowMore,
}: MarkdownContentProps) => {
    const navigate = useNavigate();
    const contentRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const maxHeight = 200;

    // Repair legacy escaped-underscore + single-line embed fences so older
    // posts still render an iframe instead of raw text.
    const normalizedMarkdown = normalizeBlogPostEmbedMarkdown(markdown ?? "");

    // Tables get auto-expanded — squeezing a table into 200px is unreadable.
    const hasTable = MARKDOWN_TABLE_RE.test(normalizedMarkdown);
    const showFull = expanded || hasTable || isExpanded;

    useEffect(() => {
        if (expanded || hasTable) return;
        const el = contentRef.current;
        if (!el) return;
        // Set in both directions so the toggle disappears if the markdown
        // shrinks below the truncation threshold.
        setIsOverflowing(el.scrollHeight > maxHeight);
    }, [markdown, hasTable, expanded]);

    return (
        <div className="w-full min-w-0">
            <div
                ref={contentRef}
                style={{
                    maxHeight: showFull ? "none" : maxHeight,
                    overflow: showFull ? "visible" : "hidden",
                    position: "relative",
                    transition: expanded || hasTable ? undefined : "max-height 0.3s ease",
                    overflowWrap: "anywhere",
                }}
                className={`${PROSE_CLASS} ${className ?? ""}`}
            >
                <Markdown
                    urlTransform={keepContentLinkUrls}
                    remarkPlugins={[remarkGfm, remarkDirective, remarkBlogPostEmbeds]}
                    rehypePlugins={[
                        [rehypeExternalLinks, { target: "_blank" }],
                        rehypeIndentation,
                        rehypeUnderline,
                    ]}
                    components={{
                        table: MarkdownTable,
                        a: ({ node: _node, ...props }) => {
                            const raw = props.href || "";
                            // PRTL-981: content links (article:123, or a legacy
                            // path from an older answer) resolve to the portal
                            // route; everything else as before.
                            const ref = contentRefFromHref(raw);
                            // A malformed href in one of our schemes is not a
                            // page and must never reach a protocol handler.
                            if (!ref && isContentLinkScheme(raw)) {
                                return <span>{props.children}</span>;
                            }
                            const href = ref ? contentRefWebPath(ref) : raw;
                            const isInternal = href.startsWith("/");

                            return (
                                <a
                                    {...props}
                                    className="text-brand-alice hover:text-brand-azure hover:underline!"
                                    {...(!isInternal && {
                                        target: "_blank",
                                        rel: "noopener noreferrer",
                                    })}
                                    onClick={
                                        isInternal
                                            ? (e) => {
                                                  e.preventDefault();
                                                  navigate({
                                                      to: href,
                                                  } as unknown as Parameters<typeof navigate>[0]);
                                              }
                                            : undefined
                                    }
                                />
                            );
                        },
                    }}
                >
                    {normalizedMarkdown}
                </Markdown>
            </div>

            {/* Render the toggle whenever truncation would apply (and the
             *  caller hasn't pinned it open via `expanded` or table-auto-
             *  expand) so users can collapse back after expanding. */}
            {isOverflowing && !expanded && !hasTable && (
                <button
                    type="button"
                    onClick={() => (onShowMore ? onShowMore() : setIsExpanded((prev) => !prev))}
                    className="text-current underline"
                    style={{
                        marginTop: 10,
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: "0.875rem",
                    }}
                >
                    {isExpanded ? "Show less" : "Show more"}
                </button>
            )}
        </div>
    );
};
